import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderIconTile,
  PageHeaderTitle,
  PageHeaderTabs,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { Navigate } from 'react-router-dom';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { AnalysisTabs } from '../components/analysis-tabs';
import { DeliverabilityTable } from './components/deliverability-table';
import { CustomSMTPEmptyState } from './components/custom-smtp-empty-state';
import { useDeliverabilityData, useHasMailServers } from './hooks';
import { generatePath } from '@/services/router/generate-path';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';

export function DeliverabilityPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const auth = useAuth();
  const { track } = useAnalytics();

  const hasUnlimitedAccess = hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.DeliverabilityAnalytics);
  const is2026LayoutRefresh = useFeatureFlag(FeatureFlag.LayoutRefresh2026);

  const {
    rows,
    dateRange,
    searchValue,
    sortingValue,
    isLoading,
    isError,
    hasNoData,
    handleDateRangeChange,
    handleSearch,
    handleSortingChange,
  } = useDeliverabilityData();

  const { hasMailServers, isLoading: isLoadingMailServers } = useHasMailServers();

  usePageViewTracking(() => {
    track('deliverability', 'viewed', {
      workspaceId,
      hasUnlimitedAccess,
    });
  });

  if (!hasUnlimitedAccess && !auth.account.is_masq) {
    return (
      <Navigate
        to={generatePath('/workspaces/:workspaceId/journeys/analysis', { workspaceId: workspaceId ?? 0 })}
        replace
      />
    );
  }

  return (
    <>
      <PageTitle title="Deliverability" />
      <Page style={{ width: '100%' }}>
        <PageHeader>
          {is2026LayoutRefresh ? <PageHeaderIconTile name="analysis" /> : null}
          <PageHeaderTitle>Analysis</PageHeaderTitle>
          <PageHeaderTabs>
            <AnalysisTabs />
          </PageHeaderTabs>
        </PageHeader>
        <PageBody>
          {hasMailServers ? (
            <CustomSMTPEmptyState />
          ) : (
            <DeliverabilityTable
              data={rows}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              isLoading={isLoading || isLoadingMailServers}
              isError={isError}
              searchValue={searchValue}
              onSearch={handleSearch}
              sortingValue={sortingValue}
              onSortingChange={handleSortingChange}
              hasNoData={hasNoData}
            />
          )}
        </PageBody>
      </Page>
    </>
  );
}
